import { AxiosResponse } from 'axios';
import { FBaseService } from '@kasasa/fbase-components/lib';
import { ConfigVersion } from './ConfigVersion';

export default class ConfigVersionResource extends FBaseService {
  public async getOne(fiId: string, versionId: string): Promise<ConfigVersion> {
    const response: AxiosResponse = await this.apiClient.get(`/api/rcx/config/${fiId}/config-version/${versionId}`);
    return response.data.data;
  }

  public async getAll(fiId: string): Promise<ConfigVersion[]> {
    let versions: ConfigVersion[] = [];
    try {
      const response: AxiosResponse = await this.apiClient.get(`/api/rcx/config/${fiId}/config-version`);
      versions = response.data.data;
    } catch (error) {
      console.dir(error);
      // Will 404 if the fi has never been populated
      if (error.response.status !== 404 && error.response.status !== 422) {
        throw error;
      }
    }

    if (versions.length === 0) {
      versions = [await this.createInitialVersion(fiId)];
    }

    return versions;
  }

  public async insertVersion(fiId: string, version: ConfigVersion): Promise<ConfigVersion> {
    const response: AxiosResponse = await this.apiClient.post(`/api/rcx/config/${fiId}/config-version`, version);
    return response.data.data;
  }

  public async getCurrent(fiId: string): Promise<ConfigVersion> {
    const configVersions: ConfigVersion[] = await this.getAll(fiId);
    configVersions.sort((a, b) => {
      if (new Date(a.startDate).valueOf() < new Date(b.startDate).valueOf()) {
        return 1;
      }
      if (new Date(a.startDate).valueOf() > new Date(b.startDate).valueOf()) {
        return -1;
      }
      throw new Error('server returned ConfigVersion collection with unexpected startDate unique constraint violation');
    });


    const currentTimestamp = new Date();
    const currentDate = new Date(currentTimestamp.getFullYear(),
      currentTimestamp.getMonth(), currentTimestamp.getDate());

    const pastOrCurrentConfigVersions = configVersions.filter(
      c => new Date(c.startDate).valueOf() <= currentDate.valueOf(),
    );

    if (pastOrCurrentConfigVersions.length > 0) {
      // return the most recent start date equal to or less than the current date
      return pastOrCurrentConfigVersions[0];
    }
    // otherwise return whatever future start date is closest to the current date
    return configVersions[configVersions.length - 1];
  }

  private async createInitialVersion(fiId: string): Promise<ConfigVersion> {
    const initialCoreVersion: ConfigVersion = {
      id: null,
      name: 'Initial Core',
      startDate: '2015-01-01',
    };

    const createdVersion: ConfigVersion = await this.insertVersion(fiId, initialCoreVersion);
    return createdVersion;
  }

  public async updateVersion(fiId: string, version: ConfigVersion): Promise<ConfigVersion> {
    const response: AxiosResponse = await this.apiClient.put(`/api/rcx/config/${fiId}/config-version/${version.id}`, version);
    return response.data.data;
  }

  public async deleteVersion(fiId: string, version: ConfigVersion): Promise<ConfigVersion> {
    const response: AxiosResponse = await this.apiClient.delete(`/api/rcx/config/${fiId}/config-version/${version.id}`);
    return response.data.data;
  }
}
